/**
 * 牌桌嘴炮：固定短语、表情闭集、「催一下」的规则常量。
 *
 * 服务端与客户端共用这一份：短语文字由服务端查表写进 `ChatEntry.text`（客户端只发编号，
 * 所以短语通道不可能被拿来塞任意文本），客户端用同一张表画按钮、用同一组数字做预节流，
 * 两边不会漂移。零依赖，不 import 任何协议类型（view.ts 反过来从这里取 `EmoteId`）。
 */

/**
 * 短语编号。**顺序即按钮顺序**（3 行 × 4 列，行序见 `PHRASE_ROWS`）。
 *
 * ★ 只能追加或替换文字，不要复用旧编号表达新意思：房间里的聊天历史（内存 100 条）
 *   存的是编号 + 当时的文字，老客户端还可能按编号渲染样式。
 */
export const PHRASE_IDS = [
  'hurry',
  'asleep',
  'wilted',
  'gone',
  'noWay',
  'really',
  'thatsIt',
  'steal',
  'lucky',
  'respect',
  'badHand',
  'revenge',
] as const

export type PhraseId = (typeof PHRASE_IDS)[number]

/**
 * 短语语音的声音。账号上自选（头像面板里的「男声 / 女声」），没选过按 `DEFAULT_VOICE`；
 * 机器人按名字固定分配。播放时跟着**发短语的人**走，所以服务端把它写进 `ChatEntry.voice`。
 */
export const VOICE_IDS = ['male', 'female'] as const

export type VoiceId = (typeof VOICE_IDS)[number]

export const DEFAULT_VOICE: VoiceId = 'male'

export function isVoiceId(x: unknown): x is VoiceId {
  return typeof x === 'string' && (VOICE_IDS as readonly string[]).includes(x)
}

/** 每条 ≤5 个字：375 宽下一格约 80px，14px 字号放得下，气泡里也不会被截断。 */
export const PHRASE_TEXT: Readonly<Record<PhraseId, string>> = {
  hurry: '快点',
  asleep: '睡着了？',
  wilted: '花都谢了',
  gone: '溜了溜了',
  noWay: '劳资不信',
  really: '真的假的',
  thatsIt: '就这？',
  steal: '偷鸡是吧',
  lucky: '这都能中？',
  respect: '牛逼',
  badHand: '手太臭了',
  revenge: '下把干你',
}

/** 一行一组，不写组名（省宽度）；`label` 只给读屏用。 */
export const PHRASE_ROWS: readonly { readonly label: string; readonly ids: readonly PhraseId[] }[] = [
  { label: '催', ids: ['hurry', 'asleep', 'wilted', 'gone'] },
  { label: '怼', ids: ['noWay', 'really', 'thatsIt', 'steal'] },
  { label: '叹', ids: ['lucky', 'respect', 'badHand', 'revenge'] },
]

export function isPhraseId(x: unknown): x is PhraseId {
  return typeof x === 'string' && (PHRASE_IDS as readonly string[]).includes(x)
}

/**
 * 表情闭集。客户端不得自定义——否则等于开了一条不受 `chatEnabled` 管控、
 * 绕过聊天限流的任意文本通道。
 *
 * `nudge`（催一下）不是表情：它有目标座位、有服务端校验、有被催方反馈，
 * 只是借用 emote 的下行形状与限流桶。
 */
export const EMOTE_IDS = [
  'thumbsUp',
  'clap',
  'laugh',
  'cry',
  'angry',
  'shock',
  'fire',
  'chips',
  'clock',
  'nudge',
] as const

export type EmoteIdOf = (typeof EMOTE_IDS)[number]

export const TALK = {
  /** 输入框上限（服务端仍按 CHAT_MAX_CHARS 截断，这里是交互上限） */
  CHAT_INPUT_MAX: 60,
  /** 打字桶：1 条 / 2s（服务端 RATE.CHAT_* 与客户端预节流同一个数） */
  CHAT_MIN_INTERVAL_MS: 2_000,
  /** 短语桶：3 条 / 5s，斗嘴连发不被打字那边卡住 */
  PHRASE_BURST: 3,
  PHRASE_PER_SEC: 0.6,
  /** 对方想满这么久才能催 */
  NUDGE_AFTER_MS: 8_000,
  /** 面对全下或大注（toCall ≥ 剩余筹码一半）时放宽到这么久 */
  NUDGE_AFTER_HEAVY_MS: 15_000,
  /** 倒计时只剩这么多时不能催：系统已经在警告了，再催只会让人慌着点错 */
  NUDGE_CLOSE_BEFORE_DEADLINE_MS: 6_000,
  /** 一个人每一步最多被催几次 */
  NUDGE_MAX_PER_TURN: 3,
  /** 同一个目标两次被催的最小间隔 */
  NUDGE_TARGET_SPACING_MS: 4_000,
  /** 服务端时间比这老的社交事件不冒气泡、不震动（断线补发 / 重连） */
  EVENT_MAX_AGE_MS: 6_000,
} as const

/**
 * 这一步催人要等多久。公开信息算的（下注额与筹码谁都看得见），不泄露牌力（§9.7）。
 */
export function nudgeWaitMs(toCall: number, stack: number, callIsAllIn: boolean): number {
  const heavy = callIsAllIn || (stack > 0 && toCall * 2 >= stack)
  return heavy ? TALK.NUDGE_AFTER_HEAVY_MS : TALK.NUDGE_AFTER_MS
}
